import ConnectedAppManifest from 'js/connected-apps/connected-app-manifest';

const useCases = [{
	key: 'featured',
	label: "Featured",
	subtitle: `Templates picked by the Visor team`,
	colorTheme: {
		primary: 'blue',
	},
},{
	key: 'cross-team-dashboards',
	label: "Cross-team dashboards",
	subtitle: `Work better together across different teams by getting everyone on the same page.`,
	colorTheme: {
		primary: 'green',
	},
},{
	key: 'team-dashboards',
	label: "Team dashboards",
	subtitle: `Improve speed of decision-making on your team with shared dashboards.`,
	colorTheme: {
		primary: 'orange',
	},
},{
	key: 'data-migration',
	label: "Data migration",
	subtitle: `Reduce cost and risk of data migration by using common spreadsheet capabilities.`,
	colorTheme: {
		primary: 'blue',
	},
},{
	key: 'productivity',
	label: 'Productivity',
	subtitle: `Save time so you can work smarter.`,
	colorTheme: {
		primary: 'orange',
	},
}];

const roles = [{
	key: 'projectManagement',
	label: "Project Management",
},{
	key: 'productManagement',
	label: "Product Management",
},{
	key: 'sales',
	label: "Sales",
},{
	key: 'marketing',
	label: "Marketing",
}];

const supportedApps = Object.keys(ConnectedAppManifest.apps).filter(function toBeFiltered(appName){
	return ConnectedAppManifest.apps[appName].enabled;
}).map(function getStruct(appName){
	return {
		key: appName,
		label: `${ConnectedAppManifest.apps[appName].label}`,
	};
});

supportedApps.push({
	key: 'visor',
	label: 'Visor data',
});

const templates = [
{	
	status: 'Done!',
	title: 'Work on the most important projects',
	subtitle: `Link support tickets from Hubspot Service with Jira Issues to find patterns among deals that didn't close.`,
	apps: ['jira', 'salesforce', 'hubspot'],
	description: [
		//Problem and proposed solution
		`When your customers submit tickets in one system and you plan your roadmap in another, it's harder to connect real customer demands and issues with your roadmap. Using this Visor template, your teams can see how your future roadmap of projects will impact the stream of requests and issues coming in from customers.`,
		//Data source setup + custom visor fields
		`To set up this template, line up your Hubspot tickets on the left side of the workbook.  Import the Hubspot tickets you'd like to analyze.  Then, add a linking column for Jira issues. As you review each feedback item, you can link it on the right with the corresponding Jira issue.`,
		//Visor features around custom views
		`Use the tabs at the bottom of the workbook to highlight and filter specific themes.  Each tab can be a theme you're trying to support with Hubspot support tickets.`,
		//Syncing
		`As the status of tickets changes, Visor syncing will pull in the latest values so you can stay abreast of when and how these issues are being addressed.`,
		//Sharing
		`This is a great resource to share with the product team, so they know the impact of what they're building. It's also a good supporting reference for managers and executives who want to understand the basis behind certain initiatives.`,],
	useCases: ['cross-team-dashboards'],
	roles: ['sales', 'productManagement',],
	assets:[{
		src: require('assets/marketing/templates/Work on the most important projects.gif'),
	}],
	count: '500+',	
},
{
	status: 'Done!',
	title: 'Improve the customer onboarding experience',
	subtitle: 'Keep a high-level view of your customer onboardings in one centralized dashboard to keep everyone abreast of the latest status.',
	apps: ['jira', 'hubspot', 'salesforce'],
	description: [
		//Problem and proposed solution
		`Customer implementation and onboarding is crucial because it establishes the initial impression while also ensuring that your customers have all the prerequisites to success in place. When your sales and implementation teams use different tools to track their work, important context can go missing.  Creating a centralized dashboard in Visor lets you bring in the relevant information from each place. In this template, we assume your customers are tracking their implementation with Hubspot issues, your sales team uses Salesforce CRM, and your implementation team us tracking their work in Jira.`,
		//Data source setup + custom visor fields
		`To set up this view, pull in the client-facing tickets on the lefthand side.  We'll view things from the client's perspective first.  Next, add a column for the Salesforce account matching each ticket.  This provides your team one-click access to dive into the Salesforce account for more details. Then, add columns to link the Jira issues your team is tracking each project with internally.  You'll likely want to pull in some other context fields, such as the issue status from Jira or details about the account from Salesforce.`,
		//Visor features around custom views
		`In addition to the main dashboard that everyone can see showing all of the in-progress implementations, you can also use Visor's gantt view to plot dates indicating when an implementation began and ended.  This will let you more easily idenitfy implementations that may have lagged.  You can also import Opportunites from salesforce with their close dates in order to plan out future implementations.`,
		//Syncing
		`When Visor syncs, it will automatically pull in the latest details from all the apps you have connected.  You can have the implementation team members add comments in Visor on individual projects to provide context to other teams.`,		
		//Sharing
		`Using access controls built into Visor, share this workbook with the teams who need it.`,],
	useCases: ['cross-team-dashboards'],
	roles: ['sales', 'productManagement',],
	assets:[{
		src: require('assets/marketing/templates/Bring people together.gif'),		
	}],
	count: '200+',
},
{	
	title: 'Show the impact of your work',
	subtitle: 'Tie the work you and your team have accomplished (or are planning) to the larger picture (OKRs).',
	apps: ['jira',],
	description: [
		//Problem and proposed solution
		`It's challening keeping sight of the bigger picture goals when you're moving quickly.  Nonetheless, explaining how your team's contributions are driving larger organizational priorities is important both for motivating the team doing it as well as showing the team's impact to exteranl stakeholders`,
		//Data source setup + custom visor fields
		`Line up your Jira issues on the left.  Generally you'll want to pull in your Epics, if that's how your team plans.  Then you can pull in the necessary fields from Jira to help contextualize each: assignee, status, priority.  Finally, add a Visor dropdown field.  If each of your issues from Jira could support multiple higher-level goals, be sure to select the multi-select option when making the new Visor dropdown field.  Finally, add all of your larger goals, initiatives, or OKRs as choices to the drop-down field. Be sure to add formatting for each of the different choices so they are easy to distinguish.`,
		//Visor features around custom views
		`Once you have your data in place, you can proceed to defining the views you'd like to share.  Consider all of the stakeholders who will be viewing, and think about what each will care about.  You can create unique views for each.`,
		//Syncing
		`As data changes in Jira, such as the statuses of Epics, you'll be able to pull in those updates by syncing.`,
		//Sharing
		`When you're ready to share, click on the Share button on the top of the workbook and grant access to your relevant team members.`,],
	useCases: ['team-dashboards'],
	roles: ['sales', 'productManagement',],
	assets:[{
		src: require('assets/marketing/templates/Show the impact of your work.gif'),
	}],
	count: '1500+',
},
{	
	title: 'Keep customers better informed of product updates',
	subtitle: 'For customers and prospects (tracked in Salesforce) who are anticipating completed Jira items, use this workbook template to plan your outreach.',
	apps: ['salesforce', 'jira'],
	description: [
		`Impress your customers and prospects by remembering what they want.  Follow up at the right time to tell them when it's available. This template guides you to create a dashboard to keep track of the interest expressed by your customers and prospects.`,
		`Start by adding the linking column for Salesforce contacts.  Pull in helpful fields such as email address.  Then add a column for Jira issues, including columns for the status.  You may also wish to add the Jira owner so that you can follow-up with the issue owner to get further details.  Lastly, add a Visor-only dropdown field to track your outreach status, inlcuding options like "Waiting on Issue" and "Email Sent."`,
		`Consider sharing this workbook with the teams responsible for working on the Jira issues.  It may help them understand the value of the work they're doing. `],
	useCases: ['cross-team-dashboards'],
	roles: ['sales', 'productManagement',],
	assets:[{
		src: require('assets/marketing/templates/Keep customers better informed of product updates.gif'),
	}],
	count: '300+',
},


































{	
	title: 'Justify product roadmap items with Hubspot deals',
	subtitle: `Link deals from Hubspot with Jira Issues to find patterns among deals that didn't close.`,
	apps: ['jira', 'hubspot'],
	description: [
		`It's crucial to understand your wins and losses.  You can use Visor's multi-app capabilities to summarize and then tie together loss reasons to the resolutions you have planned.  For teams who use Hubspot CRM for deals and use Jira to track what they plan to work on, a single Visor workbook can bring those two data sources together.`,
		`To set up this template, line up your Hubspot deals on the left side of the workbook.  Import the Hubspot deals you'd like to analyze, including the field you use for loss reason.  Then, add a linking column for Jira issues. As you review each feedback item, you can link it on the right with the corresponding Jira issue.`],
	useCases: ['cross-team-dashboards'],
	roles: ['sales', 'productManagement',],
	assets:[{
		src: require('assets/marketing/templates/Loss analysis - hubspot.gif'),
	}],
	count: '150+',
},
{	
	title: 'Justify product roadmap items with Salesforce opportunities',
	subtitle: 'Identify the most common reasons for losses from Salesforce opportunities, and then connect each with a potential resolution from Jira.',
	apps: ['salesforce', 'jira'],
	description: [
		`It's crucial to understand your wins and losses.  You can use Visor's multi-app capabilities to summarize and then tie together loss reasons to the resolutions you have planned.  For teams who use Hubspot CRM for deals and use Jira to track what they plan to work on, a single Visor workbook can bring those two data sources together.`,
		`To set up this template, line up your Hubspot deals on the left side of the workbook.  Import the Hubspot deals you'd like to analyze, including the field you use for loss reason.  Then, add a linking column for Jira issues. As you review each feedback item, you can link it on the right with the corresponding Jira issue.`],
	useCases: ['cross-team-dashboards'],
	roles: ['sales', 'productManagement',],
	assets:[{
		src: require('assets/marketing/templates/loss analysis - sf.gif'),
	}],
	count: '200+',
},
{	

	title: 'Create a personal pipeline dashboard for Salesforce',
	subtitle: 'Save time by running your personal pipeline in a Visor workbook. Easily keep tabs on important dates in a Gantt view and present your pipeline stages in diffrerent Table views.',
	apps: ['salesforce',],
	description: [
		`If you're like most, you'd rather track your deals in a spreadsheet, but your team needs data in Salesforce.  By using a Visor workbook with Salesforce opportunities, you can both be happy.`,
		`Start out by adding the Salesforce opportunity column in the Visor workbook.  Add any of the important fields you track, such as deal stage, next action, next action date, close date, amount, probability, and notes.  You can also now add personal Visor-only fields that just you can see, such as personal notes, tags, or priorities.`,
		`For faster prioritization, create tabs at the bottom for each deal stage.  Create a new Table view and then apply the filters to match the stage (or stages) you want to show on this tab.  This will help you quickly see what oppotunities need specific next actions.`,
		`You can easily change values in your workbook and sync these back to Salesforce.  For example, if you change a deal amount, you can right click on that cell and sync that back to Salesforce.  If you've made a number of changes to the same workbook, you can also click the Sync button at the top of the workbook to sync back all of your changes.`,		
		`To keep the rest of your team or your executives informed, create sharable tabs in Visor with the information relevant to each stakeholder.  Grant them commenting access so they can view and add comments.  Drop comments on any fields that require further context, such as dates that get pushed or amounts that are subject to variability.`,],
	useCases: ['team-dashboards'],
	roles: ['sales',],
	assets:[{
		src: require('assets/marketing/templates/Flexibility to work how you want.gif'),
	}],
	count: '2000+',
},
{	
	title: 'Create a personal pipeline dashboard for Hubspot',
	subtitle: 'Save time by running your personal pipeline in a Visor workbook. Easily keep tabs on important dates in a Gantt view and present your pipeline stages in diffrerent Table views.',
	apps: ['hubspot',],
	description: [
		`If you're like most, you'd rather track your deals in a spreadsheet, but your team needs data in Hubspot.  By using a Visor workbook with Hubspot deals, you can both be happy.`,
		`Start out by adding the Hubspot deal column in the Visor workbook.  Add any of the important fields you track, such as deal stage, next action, next action date, close date, amount, probability, and notes.  You can also now add personal Visor-only fields that just you can see, such as personal notes, tags, or priorities.`,
		`For faster prioritization, create tabs at the bottom for each deal stage.  Create a new Table view and then apply the filters to match the stage (or stages) you want to show on this tab.  This will help you quickly see what oppotunities need specific next actions.`,
		`You can easily change values in your workbook and sync these back to Hubspot.  For example, if you change a deal amount, you can right click on that cell and sync that back to Hubspot.  If you've made a number of changes to the same workbook, you can also click the Sync button at the top of the workbook to sync back all of your changes.`,		
		`To keep the rest of your team or your executives informed, create sharable tabs in Visor with the information relevant to each stakeholder.  Grant them commenting access so they can view and add comments.  Drop comments on any fields that require further context, such as dates that get pushed or amounts that are subject to variability.`,],
	useCases: ['team-dashboards'],
	roles: ['sales',],
	assets:[{
		src: require('assets/marketing/templates/Flexibility to work how you want.gif'),
	}],
	count: '2500+',
},
{	
	title: 'Get alignment around engineering tasks in Jira',
	subtitle: 'Improve communication with your engineering team by creating a workbook with multiple diffrent views to help the engineering team understand priorities.',
	apps: ['jira',],
	description: [
		//Problem and proposed solution
		`It can be challenging keeping teams aware of what the overall priorities are and giving a ten-thousand foot view of the tasks and prioroties. Jira's views are rigid, and don't give you the ability to add fields you may need to categorize your data easily. By using a Visor workbook, you can present the data more cleanly and flexibly, so that you get faster, stronger alignment among the team.`,
		//Data source setup + custom visor fields
		`Start out by importing the existing Jira issues to your new workbook. Include the relevant fields you want to show alongside each.  If your team uses dates, you'll be able to generate gantt charts or timelines like the one shown above.`,
		//syncing
		`You can drag these items around the timeline.  If these are driven by dates imported from Jira, they'll sync both ways with Jira.  As you change the dates in Visor (including by dragging), you can then click sync and push those changes up to Jira.  Likewise, if changes are made in Jira to the dates and you press sync in Visor, those changes will come into visor..`,		
		//Sharing
		`To keep the rest of your team informed, create sharable tabs in Visor with the information relevant to each stakeholder.  Grant them commenting access so they can view and add comments.  Drop comments on any fields that require further context, such as dates that get pushed or statuses that require further explanation.`,],
	useCases: ['team-dashboards'],
	roles: ['sales',],
	assets:[{
		src: require('assets/marketing/templates/Please DO touch the artwork.gif'),
	}],
	count: '500+',
},];

const exampleTemplate = [
{	
	title: '',
	subtitle: '',
	apps: [],
	description: [
		//Problem and proposed solution
		``,
		//Data source setup + custom visor fields
		``,
		//Visor features around custom views
		``,
		//Syncing
		``,		
		//Sharing
		``,],
	useCases: [],
	roles: [],
	assets:[{
		src: require('assets/marketing/templates/Bring people together.gif'),
	}]
},
];

const combinatorials = {
	apps:{
		jira:{
			label: "Jira",
			entities:{
				issues:{
					labelSingular: "issue",
					labelPlural: "issues",
					attributes:{
						commonBulkFields: ['priority', 'status', 'due date',],
						commonMigrationTargets: [{
							app: 'hubspot',
							entity: 'tickets',
						},{
							app: 'hubspot',
							entity: 'customObjects',
						},{
							app: 'salesforce',
							entity: 'customObjects',
						}],
					},					
				}
			}
		},
		salesforce:{
			label: "Salesforce",
			entities:{
				contacts:{
					labelSingular: "contact",
					labelPlural: "contacts",
					commonMigrationTargets: [{
						app: 'hubspot',
						entity: 'contacts',
					}],
				},
				accounts:{
					labelSingular: "account",
					labelPlural: "accounts",
					commonBulkFields: ['category',],
					commonMigrationTargets: [{
						app: 'hubspot',
						entity: 'companies',
					}],
				},
				leads:{
					labelSingular: "lead",
					labelPlural: "leads",
					commonBulkFields: ['lead source',],
					commonMigrationTargets: [{
						app: 'hubspot',
						entity: 'contacts',
					}],
				},
				opportunities:{
					labelSingular: "opportunity",
					labelPlural: "opportunities",
					commonBulkFields: ['stage', 'close date',],
					commonMigrationTargets: [{
						app: 'hubspot',
						entity: 'deals',
					}],
				},
				customObjects:{
					labelSingular: "custom object",
					labelPlural: "custom objects",
					commonBulkFields: ['field',],
					commonMigrationTargets: [{
						app: 'hubspot',
						entity: 'customObjects',
					},{
						app: 'jira',
						entity: 'issues',
					}],
				}
			}
		},
		hubspot:{
			label: "Hubspot",
			entities:{
				companies:{
					labelSingular: "company",
					labelPlural: "companies",
					commonMigrationTargets: [{
						app: 'salesforce',
						entity: 'accounts',
					}],
				},
				contacts:{
					labelSingular: "contact",
					labelPlural: "contacts",
					commonMigrationTargets: [{
						app: 'salesforce',
						entity: 'contacts',
					},{
						app: 'salesforce',
						entity: 'leads',
					}],
				},
				deals:{
					labelSingular: "deal",
					labelPlural: "deals",
					commonBulkFields: ['stage', 'close date',],
					commonMigrationTargets: [{
						app: 'salesforce',
						entity: 'opportunities',
					}],
				},
				tickets:{
					labelSingular: "ticket",
					labelPlural: "tickets",
					commonBulkFields: ['status',],
					commonMigrationTargets: [{
						app: 'jira',
						entity: 'issues',
					}],
				},
				customObjects:{
					labelSingular: "custom object",
					labelPlural: "custom objects",
					commonBulkFields: ['field',],
					commonMigrationTargets: [{
						app: 'salesforce',
						entity: 'customObjects',
					},{
						app: 'jira',
						entity: 'issues',
					}],
				}
			}
		}
	},
	autoTemplates:[{
		//Bulk reassign
		type: 'single',
		title(appName, appDetails, entityName, entityDetails){
			return `Bulk reassign ${appDetails.label} ${entityDetails.labelPlural}`;
		},
		subtitle(appName, appDetails, entityName, entityDetails){
			return `Save time by bulk reassigning ${appDetails.label} ${entityDetails.labelPlural} using copy and paste with Visor.`;
		},
		apps(appName, appDetails, entityName, entityDetails){
			return [appName];
		},
		description(appName, appDetails, entityName, entityDetails){
			return `If you ever needed to bulk reassign ${entityDetails.labelPlural} within ${appDetails.label}, you know how time-consuming it can be.  You either have to do each one individually or jump through hoops.  In a spreadsheet, it would be as easy as copying and pasting -- or just dragging the value down.  Visor makes it that easy.  Just import the ${entityDetails.labelPlural} you want into a new Visor workbook.  Add the owner column you want to bulk update. Set one item to the new value.  Once that's set, you can copy and paste the value down across the others.`;
		},
		useCases(appName, appDetails, entityName, entityDetails){
			return ['productivity'];
		},
		roles(appName, appDetails, entityName, entityDetails){
			return [];
		},
		assets(appName, appDetails, entityName, entityDetails){
			return [{
				src: require('assets/marketing/templates/Fully immersive integrations.gif'),
			}]
		},
		count(){
			return '150+';
		}
	},{
		//Bulk set fields
		getTemplates(appName, appDetails, entityName, entityDetails){
			let retTemplates = [];
			const templateGen = {
				title(appName, appDetails, entityName, entityDetails, bulkFieldName){
					return `Bulk change the ${bulkFieldName} of ${appDetails.label} ${entityDetails.labelPlural}`;
				},
				subtitle(appName, appDetails, entityName, entityDetails, bulkFieldName){
					return `Save time by bulk updating the ${bulkFieldName} of ${appDetails.label} ${entityDetails.labelPlural} using copy and paste with Visor.`;
				},
				apps(appName, appDetails, entityName, entityDetails, bulkFieldName){
					return [appName];
				},
				description(appName, appDetails, entityName, entityDetails, bulkFieldName){
					return `If you ever needed to bulk change the ${bulkFieldName} field on ${entityDetails.labelPlural} within ${appDetails.label}, you know how time-consuming it can be.  You either have to do each one individually or jump through hoops.  In a spreadsheet, it would be as easy as copying and pasting -- or just dragging the value down.  Visor makes it that easy.  Just import the ${entityDetails.labelPlural} you want into a new Visor workbook.  Add the ${bulkFieldName} field you want to bulk update. Set one item to the new value.  Once that's set, you can copy and paste the value down across the others.`;
				},
				useCases(appName, appDetails, entityName, entityDetails, bulkFieldName){
					return ['productivity'];
				},
				roles(appName, appDetails, entityName, entityDetails, bulkFieldName){
					return [];
				},
				assets(appName, appDetails, entityName, entityDetails, bulkFieldName){
					return [{
						src: require('assets/marketing/templates/Fully immersive integrations.gif'),
					}]
				},
				count(){
					return '250+';
				}
			};
			if(entityDetails.commonBulkFields && entityDetails.commonBulkFields.length > 0){
				for(let bulkFieldIndex = 0; bulkFieldIndex < entityDetails.commonBulkFields.length; bulkFieldIndex++){					
					const bulkFieldName = entityDetails.commonBulkFields[bulkFieldIndex];
					let template = {};
					for(let prop in templateGen){
						template[prop] = templateGen[prop](appName, appDetails, entityName, entityDetails, bulkFieldName);
					}
					retTemplates.push(template);
				}
			}

			return retTemplates;
		}
	},{
		//migrate data
		getTemplates(appName, appDetails, entityName, entityDetails){
			let retTemplates = [];
			const templateGen = {
				title(appName, appDetails, entityName, entityDetails, commonMigrationTarget){
					const destinationAppDetails = combinatorials.apps[commonMigrationTarget.app];
					const destinationEntityDetails = destinationAppDetails.entities[commonMigrationTarget.entity];

					return `Migrate ${appDetails.label} ${entityDetails.labelPlural} to ${destinationAppDetails.label} ${destinationEntityDetails.labelPlural}`;
				},
				subtitle(appName, appDetails, entityName, entityDetails, commonMigrationTarget){	
					const destinationAppDetails = combinatorials.apps[commonMigrationTarget.app];
					const destinationEntityDetails = destinationAppDetails.entities[commonMigrationTarget.entity];

					return `Save time and frustration by using Visor's easy way to migrate data from ${appDetails.label} ${entityDetails.labelPlural} to ${destinationAppDetails.label} ${destinationEntityDetails.labelPlural}`;
				},
				apps(appName, appDetails, entityName, entityDetails, commonMigrationTarget){
					return [appName, commonMigrationTarget.app];
				},
				description(appName, appDetails, entityName, entityDetails, commonMigrationTarget){
					const destinationAppDetails = combinatorials.apps[commonMigrationTarget.app];
					const destinationEntityDetails = destinationAppDetails.entities[commonMigrationTarget.entity];

					return `When you have data in ${appDetails.label} ${entityDetails.labelPlural} and you want to copy some (or all) of it over to ${destinationAppDetails.label} ${destinationEntityDetails.labelPlural}, there isn't always an easy way to do it. But Visor makes that easier.  First, add to the Visor workbook the ${appDetails.label} ${entityDetails.labelPlural} you want to transfer, including all the columns of data from ${appDetails.label} that you'll want to copy.  Then add columns for ${destinationEntityDetails.labelPlural} from ${destinationAppDetails.label}.  Copy over the data to the corresponding ${destinationAppDetails.label} columns.  For columns in ${destinationAppDetails.label} where there is no data from ${appDetails.label} to copy, you can either leave them blank or fill in new values.  When you're ready to push the new ${entityDetails.labelPlural} up to ${destinationAppDetails.label}, just press sync for ${appDetails.label} at the top of the workbook.  That's it!`;
				},
				useCases(appName, appDetails, entityName, entityDetails, commonMigrationTarget){
					return ['data-migration'];
				},
				roles(appName, appDetails, entityName, entityDetails, commonMigrationTarget){
					return [];
				},
				assets(appName, appDetails, entityName, entityDetails, commonMigrationTarget){
					return [{
						src: require('assets/marketing/templates/Migrate data the easy way.gif'),
					}]
				},
				count(){
					return '200+';
				}
			};
			if(entityDetails.commonMigrationTargets && entityDetails.commonMigrationTargets.length > 0){
				for(let bulkFieldIndex = 0; bulkFieldIndex < entityDetails.commonMigrationTargets.length; bulkFieldIndex++){					
					const commonMigrationTarget = entityDetails.commonMigrationTargets[bulkFieldIndex];
					let template = {};
					for(let prop in templateGen){
						template[prop] = templateGen[prop](appName, appDetails, entityName, entityDetails, commonMigrationTarget);
					}
					retTemplates.push(template);
				}
			}

			return retTemplates;
		}
	}],
	runAll(){		
		for(let autoTemplateIndex = 0; autoTemplateIndex < combinatorials.autoTemplates.length; autoTemplateIndex++){
			const autoTemplate = combinatorials.autoTemplates[autoTemplateIndex];
			for(let appName in combinatorials.apps){
				const appDetails = combinatorials.apps[appName];
				for(let entityName in appDetails.entities){
					const entityDetails = appDetails.entities[entityName];
					if(autoTemplate.type === 'single'){
						const template = {
							title: autoTemplate.title(appName, appDetails, entityName, entityDetails),
							subtitle: autoTemplate.subtitle(appName, appDetails, entityName, entityDetails),
							apps: autoTemplate.apps(appName, appDetails, entityName, entityDetails),
							description: autoTemplate.description(appName, appDetails, entityName, entityDetails),
							useCases: autoTemplate.useCases(appName, appDetails, entityName, entityDetails),
							roles: autoTemplate.roles(appName, appDetails, entityName, entityDetails),
							assets: autoTemplate.assets(appName, appDetails, entityName, entityDetails),
						};
						templates.push(template);
					}
					else{
						const generatedTemplates = autoTemplate.getTemplates(appName, appDetails, entityName, entityDetails);
						templates.push(...generatedTemplates);
					}					
				}
			}
		}		
	}
};

combinatorials.runAll();




const generatedKeys = {};

templates.forEach(function generateURLKey(template){
	const titleLower = template.title.toLowerCase();
	const titleWords = titleLower.split(' ');
	const urlSafeWords = titleWords.map(function makeUrlSafe(word){
		return encodeURIComponent(word);
	});
	const key = urlSafeWords.join('-');
	if(key in generatedKeys){
		Assert.throwError(`Duplicate keys would be generated by template manifest: ${template.title}`);
	}
	generatedKeys[key] = true;
	template.key = key;
})

export {
	useCases,
	roles,
	supportedApps,
	templates,
};